import { generateUUID } from '@/domain/shared/generateUUID';
import { AField } from '@/domain/field/AField';
import { ETextAlign } from '@/domain/textbox/ETextAlign';
import type { TPosition2D } from '@/domain/shared/TPosition2D';
import type { TScale2D } from '@/domain/shared/TScale2D';
import type { TSelectionRange } from '@/domain/textbox/TSelectionRange';
import type { TSide } from '@/domain/shared/TSide';
import type { TSize2D } from '@/domain/shared/TSize2D';
import type { TTextLayoutPatch } from '@/domain/textbox/TTextLayoutPatch';
import type { TTextLayoutResult } from '@/domain/textbox/TTextLayoutResult';
import type { TTextStylePatch } from '@/domain/textbox/TTextStylePatch';
import type { TTextStyleResult } from '@/domain/textbox/TTextStyleResult';
import type { TTextStyleAttrs, TTextStyles } from '@/domain/textbox/TTextStyles';
import type { TTextboxFieldJSONData } from '@/domain/textbox/textboxFieldSchema';

interface ITextboxField {
    characterSpacing: number | undefined;
    color: string;
    fontFamily: string;
    fontSize: number;
    frameSize: TSize2D | undefined;
    isBold: boolean;
    isItalic: boolean;
    isLocked: boolean;
    isUnderlined: boolean;
    lineSpacing: number | undefined;
    // Sample text set by the template designer — shown as the input
    // placeholder when the user hasn't filled `text` yet. Kept
    // separate from `text` so the UI can distinguish "designer hint"
    // from "user content".
    placeholder: string | undefined;
    position: TPosition2D;
    rotationDeg: number;
    scale: TScale2D;
    side: TSide;
    size: TSize2D;
    styles: TTextStyles;
    text: string;
    textAlign: ETextAlign;
}

const DEFAULT_TEXTBOX_FIELD: ITextboxField = {
    characterSpacing: undefined,
    color: '#000000',
    fontFamily: 'AmbroiseStd-Regular',
    fontSize: 25,
    frameSize: undefined,
    isBold: false,
    isItalic: false,
    isLocked: false,
    isUnderlined: false,
    lineSpacing: undefined,
    placeholder: undefined,
    position: { x: 0, y: 0 },
    rotationDeg: 0,
    scale: { x: 1, y: 1 },
    side: 'recto',
    size: { height: 0, width: 500 },
    styles: {},
    text: '',
    textAlign: ETextAlign.Center,
};

type TTextboxFieldParameters = Partial<ITextboxField> & { id?: string };

class TextboxField extends AField implements ITextboxField {
    readonly kind = 'textbox' as const;
    characterSpacing: number | undefined;
    color: string;
    fontFamily: string;
    fontSize: number;
    isBold: boolean;
    isItalic: boolean;
    isUnderlined: boolean;
    lineSpacing: number | undefined;
    placeholder: string | undefined;
    size: TSize2D;
    styles: TTextStyles;
    text: string;
    textAlign: ETextAlign;

    constructor(params: TTextboxFieldParameters = {}) {
        const merged = { ...DEFAULT_TEXTBOX_FIELD, ...params };
        super({
            frameSize: merged.frameSize,
            id: merged.id,
            isLocked: merged.isLocked,
            position: merged.position,
            rotationDeg: merged.rotationDeg,
            scale: merged.scale,
            side: merged.side,
        });
        this.characterSpacing = merged.characterSpacing;
        this.color = merged.color;
        this.fontFamily = merged.fontFamily;
        this.fontSize = merged.fontSize;
        this.isBold = merged.isBold;
        this.isItalic = merged.isItalic;
        this.isUnderlined = merged.isUnderlined;
        this.lineSpacing = merged.lineSpacing;
        this.placeholder = merged.placeholder;
        this.size = { ...merged.size };
        // Deep, not a spread: the spread shared each line's char map, so two fields built from
        // the same styles object -- a clone, a fromJSON, or two callers passing the same
        // default -- restyled each other one character at a time.
        this.styles = _copyStyles(merged.styles);
        this.text = merged.text;
        this.textAlign = merged.textAlign;
    }

    // Either half counts: createFabricField renders `text || placeholder`, so a
    // template-seeded textbox the user has not typed into yet still has something on screen.
    hasContent(): boolean {
        return this.placeholder !== undefined || this.text !== '';
    }

    setText(text: string): void {
        this.text = text;
    }

    applyLayout(patch: TTextLayoutPatch, result: TTextLayoutResult): void {
        if (patch.align !== undefined) {
            this.textAlign = patch.align;
        }
        if (patch.charSpacing !== undefined) {
            this.characterSpacing = patch.charSpacing;
        }
        if (patch.lineHeight !== undefined) {
            this.lineSpacing = patch.lineHeight;
        }
        if (result.angleDeg !== undefined) {
            this.rotationDeg = result.angleDeg;
        }
        if (result.position) {
            this.position = { ...result.position };
        }
    }

    // With a range, the patch applies only to those chars — entity-level
    // defaults stay untouched. Without range, patch becomes the entity's
    // default. `result.styles` always reflects the new per-char state;
    // `result.position` is only set when the change triggered a reflow
    // (fontSize, italic).
    applyStyle(patch: TTextStylePatch, result: TTextStyleResult, range?: TSelectionRange): void {
        if (!range) {
            if (patch.color !== undefined) {
                this.color = patch.color;
            }
            if (patch.fontFamily !== undefined) {
                this.fontFamily = patch.fontFamily;
            }
            if (patch.fontSize !== undefined) {
                this.fontSize = patch.fontSize;
            }
            if (patch.isBold !== undefined) {
                this.isBold = patch.isBold;
            }
            if (patch.isItalic !== undefined) {
                this.isItalic = patch.isItalic;
            }
            if (patch.isUnderlined !== undefined) {
                this.isUnderlined = patch.isUnderlined;
            }
        }
        // Copied, never aliased. The engine hands back its own live styles object and mutates
        // it in place on every later edit — keeping the reference would let the engine change
        // domain state behind the entity's back, and would defeat any observer watching this
        // property: the assignment below would keep writing the same reference, so nothing
        // would look changed even though the values had.
        this.styles = _copyStyles(result.styles);
        if (result.position) {
            this.position = { ...result.position };
        }
    }

    // Style getters — return the value at `range.start` when a per-char
    // override is set, otherwise the entity-level default. `range`
    // omitted / undefined means "no selection", so always the default.
    colorAt(range?: TSelectionRange): string {
        return this._styleAt(range, 'fill') ?? this.color;
    }

    fontFamilyAt(range?: TSelectionRange): string {
        return this._styleAt(range, 'fontFamily') ?? this.fontFamily;
    }

    isBoldAt(range?: TSelectionRange): boolean {
        const weight = this._styleAt(range, 'fontWeight');
        return weight !== undefined ? weight === '700' : this.isBold;
    }

    isItalicAt(range?: TSelectionRange): boolean {
        const style = this._styleAt(range, 'fontStyle');
        return style !== undefined ? style === 'italic' : this.isItalic;
    }

    isUnderlinedAt(range?: TSelectionRange): boolean {
        const underline = this._styleAt(range, 'underline');
        return underline !== undefined ? underline : this.isUnderlined;
    }

    // Per-char font size is stored raw (Fabric convention: 4× the
    // user-facing value); the entity's own fontSize is user-facing.
    fontSizeAt(range?: TSelectionRange): number {
        const size = this._styleAt(range, 'fontSize');
        return typeof size === 'number' ? size / 4 : this.fontSize;
    }

    // Text reflow emits an updated size alongside the transform — merge
    // it in when present, otherwise the base transform (pos + rot +
    // scale) is enough.
    applyViewTransform(transform: { position: TPosition2D; rotationDeg: number; scale: TScale2D; size?: TSize2D }): void {
        super.applyViewTransform(transform);
        if (transform.size) {
            this.size = { ...transform.size };
        }
    }

    clone(): TextboxField {
        return TextboxField.fromJSON({ ...this.toJSON(), id: generateUUID() });
    }

    toJSON(): TTextboxFieldJSONData {
        return {
            // Yup uses null for absent numeric spacing / size fields; entity uses undefined.
            characterSpacing: this.characterSpacing ?? null,
            color: this.color,
            fontFamily: this.fontFamily,
            fontSize: this.fontSize,
            frameSize: this.frameSize ?? null,
            id: this.id,
            isBold: this.isBold,
            isItalic: this.isItalic,
            isLocked: this.isLocked,
            isUnderlined: this.isUnderlined,
            kind: 'textbox',
            lineSpacing: this.lineSpacing ?? null,
            placeholder: this.placeholder ?? null,
            position: this.position,
            rotationDeg: this.rotationDeg,
            scale: this.scale,
            side: this.side,
            size: this.size,
            styles: this.styles,
            text: this.text,
            textAlign: this.textAlign,
        };
    }

    private _styleAt<K extends keyof TTextStyleAttrs>(range: TSelectionRange | undefined, attr: K): TTextStyleAttrs[K] | undefined {
        if (!range) {
            return undefined;
        }
        const coord = this._firstCharCoord(range.start);
        return this.styles[coord.line]?.[coord.char]?.[attr];
    }

    // A selection range indexes the string on screen, and createFabricField renders
    // `text || placeholder` — so coordinates resolve against that same string. Resolving
    // against `text` alone sent every range to char 0 while a placeholder was showing, which
    // is what made the style panel report the first character instead of the selected one.
    private _firstCharCoord(position: number): { char: number; line: number } {
        const lines = (this.text || this.placeholder || '').split('\n');
        let cursor = Math.max(position, 0);
        let line = 0;
        // Stop on the last line rather than walking past it: Fabric emits no
        // selection:changed when characters are inserted, so a range can outlive the string
        // it was taken from, and reading `lines[line].length` off the end threw.
        while (line < lines.length - 1 && cursor > lines[line].length) {
            cursor -= lines[line].length + 1;
            line++;
        }
        return { char: Math.min(cursor, lines[line].length), line };
    }

    static fromJSON(json: TTextboxFieldJSONData): TextboxField {
        return new TextboxField({
            id: json.id,
            characterSpacing: json.characterSpacing ?? undefined,
            color: json.color,
            fontFamily: json.fontFamily,
            fontSize: json.fontSize,
            frameSize: json.frameSize ?? undefined,
            isBold: json.isBold,
            isItalic: json.isItalic,
            isLocked: json.isLocked,
            isUnderlined: json.isUnderlined,
            lineSpacing: json.lineSpacing ?? undefined,
            placeholder: json.placeholder ?? undefined,
            position: json.position,
            rotationDeg: json.rotationDeg,
            scale: json.scale,
            side: json.side,
            size: json.size,
            styles: json.styles as TTextStyles,
            text: json.text,
            textAlign: json.textAlign as ETextAlign,
        });
    }
}

// Two levels deep, which is the shape Fabric uses: line index → char index → attributes.
function _copyStyles(styles: TTextStyles): TTextStyles {
    const copy: TTextStyles = {};
    Object.entries(styles).forEach(([line, chars]) => {
        const charCopy: TTextStyles[number] = {};
        Object.entries(chars).forEach(([char, attrs]) => {
            charCopy[Number(char)] = { ...attrs };
        });
        copy[Number(line)] = charCopy;
    });
    return copy;
}

export { TextboxField };
export type { TTextboxFieldParameters };
